import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import BookCallModal from '../components/site/BookCallModal';
import ChatWidget from '../components/site/ChatWidget';
import { fetchCaseStudy } from '../lib/api';
import { ArrowLeft, Users, Clock, Layers, CheckCircle2, Quote, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useI18n } from '../i18n';
import Seo, { breadcrumbLd } from '../components/Seo';

const CaseStudyPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [cs, setCs] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [callOpen, setCallOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCs(null); setNotFound(false);
    fetchCaseStudy(slug).then(setCs).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onBookCall={() => setCallOpen(true)} />
        <div className="max-w-3xl mx-auto px-5 py-40 text-center">
          <h1 className="text-3xl font-extrabold text-slate-900">{t('caseStudy.notFound')}</h1>
          <p className="mt-3 text-slate-600">{t('caseStudy.notFoundBody')}</p>
          <Button onClick={() => navigate('/')} className="mt-6 bg-orange-500 hover:bg-orange-600">{t('caseStudy.notFoundBody') ? t('common.backHome') : 'Back to Home'}</Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!cs) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onBookCall={() => setCallOpen(true)} />
        <div className="max-w-3xl mx-auto px-5 py-40 text-center text-slate-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title={`${cs.title || cs.name || 'Case Study'} | Hipro Technologies Portfolio`}
        description={(cs.summary || cs.subtitle || cs.description || 'Explore how Hipro Technologies delivered measurable results for this client.').slice(0, 160)}
        path={`/portfolio/${slug}`}
        type="article"
        image={cs.image || cs.cover}
        jsonLd={breadcrumbLd([
          { name: 'Home', path: '/' },
          { name: 'Portfolio', path: '/#portfolio' },
          { name: cs.title || cs.name || 'Case Study', path: `/portfolio/${slug}` },
        ])}
      />
      <Navbar onBookCall={() => setCallOpen(true)} />

      {/* Hero */}
      <section className="relative pt-28 pb-14 bg-slate-950 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              'radial-gradient(900px 400px at 10% -10%, rgba(59,130,246,0.25), transparent 60%), radial-gradient(700px 400px at 100% 10%, rgba(249,115,22,0.18), transparent 60%)',
          }}
        />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-orange-400">
            <ArrowLeft className="h-4 w-4" /> {t('common.backToPortfolio')}
          </Link>
          <div className="mt-5 flex flex-wrap gap-2 text-xs">
            <span className="px-2.5 py-1 rounded-full bg-white/10 border border-white/10">{cs.industry}</span>
            <span className="px-2.5 py-1 rounded-full bg-white/10 border border-white/10">{cs.category}</span>
            <span className="px-2.5 py-1 rounded-full bg-orange-500 text-white">{cs.outcome_headline}</span>
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">{cs.title}</h1>
          <p className="mt-4 text-slate-300 max-w-3xl">{cs.summary}</p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
            <div className="rounded-xl border border-white/10 bg-white/5 p-4"><div className="text-xs text-slate-400 flex items-center gap-1"><Users className="h-3.5 w-3.5"/> {t('caseStudy.team')}</div><div className="mt-1 font-bold">{cs.team_size}</div></div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4"><div className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3.5 w-3.5"/> {t('caseStudy.duration')}</div><div className="mt-1 font-bold">{cs.duration}</div></div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4"><div className="text-xs text-slate-400 flex items-center gap-1"><Layers className="h-3.5 w-3.5"/> {t('caseStudy.client')}</div><div className="mt-1 font-bold text-sm">{cs.client}</div></div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4"><div className="text-xs text-slate-400">{t('caseStudy.industry')}</div><div className="mt-1 font-bold text-sm">{cs.industry}</div></div>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 -mt-10">
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
          <img src={cs.hero_image} alt={cs.title} className="w-full h-72 md:h-[420px] object-cover" />
        </div>
      </section>

      {/* Body */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-10">
          <div>
            <h2 className="text-2xl font-extrabold text-slate-900">{t('caseStudy.problem')}</h2>
            <p className="mt-3 text-slate-700 leading-relaxed">{cs.problem}</p>
          </div>
          <div>
            <h2 className="text-2xl font-extrabold text-slate-900">{t('caseStudy.solution')}</h2>
            <p className="mt-3 text-slate-700 leading-relaxed">{cs.solution}</p>
          </div>

          {cs.gallery?.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-4">
              {cs.gallery.map((g, i) => (
                <img key={i} src={g} alt={`gallery-${i}`} className="rounded-xl border border-slate-200 h-52 w-full object-cover" />
              ))}
            </div>
          )}

          <div>
            <h2 className="text-2xl font-extrabold text-slate-900">{t('caseStudy.outcomes')}</h2>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              {(cs.metrics || []).map((m) => (
                <div key={m.label} className="rounded-xl border border-slate-200 p-4 bg-gradient-to-br from-slate-50 to-white">
                  <div className="text-2xl font-extrabold text-slate-900">{m.value}</div>
                  <div className="mt-1 text-xs text-slate-500">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <div className="rounded-2xl border border-slate-200 p-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-orange-600">{t('caseStudy.techStack')}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(cs.tech || []).map((tech) => (
                <span key={tech} className="text-xs bg-slate-100 text-slate-800 border border-slate-200 rounded-full px-2.5 py-1">{tech}</span>
              ))}
            </div>
          </div>

          {cs.testimonial && (
            <div className="rounded-2xl border border-slate-200 bg-slate-900 text-white p-5">
              <Quote className="h-6 w-6 text-orange-400" />
              <p className="mt-3 text-sm leading-relaxed text-slate-100">“{cs.testimonial.quote}”</p>
              <div className="mt-4">
                <div className="font-semibold">{cs.testimonial.name}</div>
                <div className="text-xs text-slate-400">{cs.testimonial.role}</div>
                <div className="mt-2 flex text-amber-400">
                  {[...Array(5)].map((_, i) => (<Star key={i} className="h-4 w-4 fill-current" />))}
                </div>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5">
            <h4 className="text-lg font-bold text-slate-900">{t('caseStudy.similar')}</h4>
            <p className="mt-1 text-sm text-slate-700">{t('caseStudy.similarBody')}</p>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => setCallOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white">{t('caseStudy.bookCall')}</Button>
              <Link to="/pricing"><Button variant="outline" className="border-slate-300">{t('caseStudy.seePricing')}</Button></Link>
            </div>
          </div>

          <ul className="space-y-2 text-sm text-slate-700">
            {['Strategy & scoping','Design & prototyping','Full-stack development','QA & security','Deployment','Ongoing support'].map((f) => (
              <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> {f}</li>
            ))}
          </ul>
        </aside>
      </section>

      <Footer />
      <ChatWidget />
      <BookCallModal open={callOpen} onOpenChange={setCallOpen} />
    </div>
  );
};

export default CaseStudyPage;
